import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { getLink, LINKS } from '../../config/links';

interface NavItemProps {
  to: string;
  onClick?: () => void;
  children: React.ReactNode;
}

const NavItem: React.FC<NavItemProps> = ({ to, onClick, children }) => (
  <Link 
    to={to} 
    onClick={onClick}
    className="text-sm font-medium text-muted hover:text-ink transition-colors cursor-pointer"
  >
    {children}
  </Link>
);

export const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  const signupLink = getLink('signup');
  const loginLink = getLink('login');
  const isExternal = !!LINKS.APP_SIGNUP_URL;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${
        scrolled || isOpen ? 'bg-white border-b border-border py-3 shadow-sm' : 'bg-transparent border-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="font-display font-bold text-xl tracking-tight text-ink z-[70] relative">
            PERO Brief
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavItem to="/#como-funciona">Como funciona</NavItem>
            <NavItem to="/#inbox">Inbox</NavItem>
            <NavItem to="/#seguranca">Segurança</NavItem>
            <NavItem to="/#faq">FAQ</NavItem>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" href={loginLink} isExternal={isExternal}>
              Entrar
            </Button>
            <Button variant="primary" size="sm" href={signupLink} isExternal={isExternal}>
              Cadastrar
            </Button>
          </div>

          <button 
            className="md:hidden z-[70] relative p-2 text-ink hover:bg-subtle rounded-md transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <div 
        className={`fixed inset-0 z-[50] flex flex-col pt-24 px-6 transition-all duration-500 md:hidden ${
          isOpen 
            ? 'opacity-100 translate-y-0 visible pointer-events-auto bg-white' 
            : 'opacity-0 -translate-y-4 invisible pointer-events-none'
        }`}
      >
        <nav className="flex flex-col gap-6 text-xl font-medium border-b border-border pb-8 mb-8 mt-4">
          <Link 
            to="/#como-funciona" 
            className="py-3 text-ink hover:text-primary active:bg-subtle px-2 rounded-lg transition-colors flex items-center justify-between group"
            onClick={() => setIsOpen(false)}
          >
            Como funciona
            <span className="text-muted group-hover:text-primary transition-colors">→</span>
          </Link>
          <Link 
            to="/#inbox" 
            className="py-3 text-ink hover:text-primary active:bg-subtle px-2 rounded-lg transition-colors flex items-center justify-between group"
            onClick={() => setIsOpen(false)}
          >
            Inbox
            <span className="text-muted group-hover:text-primary transition-colors">→</span>
          </Link>
          <Link 
            to="/#seguranca" 
            className="py-3 text-ink hover:text-primary active:bg-subtle px-2 rounded-lg transition-colors flex items-center justify-between group"
            onClick={() => setIsOpen(false)}
          >
            Segurança
            <span className="text-muted group-hover:text-primary transition-colors">→</span>
          </Link>
          <Link 
            to="/#faq" 
            className="py-3 text-ink hover:text-primary active:bg-subtle px-2 rounded-lg transition-colors flex items-center justify-between group"
            onClick={() => setIsOpen(false)}
          >
            FAQ
            <span className="text-muted group-hover:text-primary transition-colors">→</span>
          </Link>
        </nav>
        
        <div className="flex flex-col gap-4 mt-auto pb-12">
            <Button variant="primary" size="lg" className="w-full justify-center h-14 text-lg font-bold" href={signupLink} isExternal={isExternal}>
              Criar minha conta
            </Button>
            <Button variant="outline" size="lg" className="w-full justify-center h-14 text-lg" href={loginLink} isExternal={isExternal}>
              Entrar
            </Button>
        </div>
      </div>
    </header>
  );
};